<template>
  <div>
    <!-- Main Header with Glass Effect -->
    <v-app-bar
      app
      fixed
      height="72"
      class="header-glass elevation-4"
      :class="{ 'header-scrolled': isScrolled }"
    >
      <div class="header-gradient"></div>

      <v-container fluid class="header-container px-6">
        <v-row align="center" no-gutters class="header-row">
          <!-- Brand Section -->
          <v-col cols="auto" class="brand-section">
            <div class="logo-container" aria-label="Ventlly brand">
              <div class="logo-wrapper">
                <img
                  src="@/assets/logo.png"
                  alt="Inventory Logo"
                  height="44"
                  class="main-logo"
                />
                <div class="logo-glow"></div>
              </div>
              <div class="brand-text hidden-sm-and-down">
                <span class="brand-name">Ventlly</span>
                <span class="brand-tagline">{{
                  DIALECT['Smart Inventory Management'][$vuetify.lang.current]
                }}</span>
              </div>
            </div>
          </v-col>

          <!-- Center Navigation -->
          <v-col class="d-flex justify-center">
            <nav class="main-navigation hidden-md-and-down">
              <router-link
                v-for="navItem in navigationItems"
                :key="navItem.path"
                :to="navItem.path"
                class="nav-link"
                :class="{ 'nav-link--active': $route.path === navItem.path }"
              >
                <v-icon small class="nav-icon">{{ navItem.icon }}</v-icon>
                {{ DIALECT[navItem.title][$vuetify.lang.current] }}
              </router-link>
            </nav>
          </v-col>

          <!-- Right Actions -->
          <v-col cols="auto" class="actions-section">
            <!-- Search Button -->

            <!-- Language Selector -->
            <v-menu v-model="langMenuOpen" offset-y left>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  class="action-btn lang-btn mr-3"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-translate</v-icon>
                </v-btn>
              </template>
              <v-card class="language-card">
                <v-list dense>
                  <v-list-item
                    v-for="(locale, i) in defaultLocale"
                    :key="i"
                    :class="{
                      'v-list-item--active':
                        $vuetify.lang.current === locale.value
                    }"
                    @click="changeLocale(locale)"
                  >
                    <v-list-item-avatar size="24">
                      <v-icon small>{{ locale.flag }}</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{
                        locale.language
                      }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>

            <!-- User Profile or Login -->
            <div :key="`auth-section-${isUserAuthenticated}`">
              <template v-if="isUserAuthenticated">
                <v-menu
                  v-model="loginMenuOpen"
                  offset-y
                  left
                  min-width="280"
                  :close-on-content-click="false"
                >
                  <template #activator="{ on, attrs }">
                    <div class="user-profile-trigger" v-bind="attrs" v-on="on">
                      <v-avatar size="40" class="user-avatar">
                        <img
                          v-if="userAvatar"
                          :src="userAvatar"
                          :alt="displayFullname"
                        />
                        <v-icon v-else color="white">mdi-account</v-icon>
                      </v-avatar>
                      <div class="user-info hidden-sm-and-down">
                        <span class="user-name">{{ truncatedName }}</span>
                        <span class="user-role">{{ userRole }}</span>
                      </div>
                      <v-icon class="dropdown-icon">mdi-chevron-down</v-icon>
                    </div>
                  </template>

                  <v-card class="user-menu-card" elevation="8">
                    <div class="user-header">
                      <v-avatar size="56" class="user-menu-avatar">
                        <img
                          v-if="userAvatar"
                          :src="userAvatar"
                          :alt="displayFullname"
                        />
                        <v-icon v-else color="white" size="32"
                          >mdi-account</v-icon
                        >
                      </v-avatar>
                      <div class="user-details">
                        <h3 class="user-menu-name">{{ displayFullname }}</h3>
                        <p class="user-menu-email">{{ displayEmail }}</p>
                        <v-chip small color="primary" outlined>{{
                          userRole
                        }}</v-chip>
                      </div>
                    </div>

                    <div v-if="displayEmail" class="email-verification-row">
                      <v-chip
                        small
                        :color="emailVerificationColor"
                        outlined
                        class="email-verification-chip"
                      >
                        <v-icon small left>{{ emailVerificationIcon }}</v-icon>
                        {{ emailVerificationLabel }}
                      </v-chip>

                      <v-btn
                        v-if="canResendVerificationEmail"
                        small
                        text
                        color="primary"
                        class="resend-verification-btn"
                        :loading="verificationResendLoading"
                        :disabled="verificationResendLoading"
                        @click.stop="resendVerificationEmail"
                      >
                        <v-icon left small>mdi-email-fast-outline</v-icon>
                        Resend email
                      </v-btn>
                    </div>

                    <v-alert
                      v-if="verificationMessage"
                      :type="verificationMessageType"
                      dense
                      text
                      class="verification-message"
                    >
                      {{ verificationMessage }}
                    </v-alert>

                    <v-divider class="my-2"></v-divider>

                    <v-list dense class="user-menu-list">
                      <v-list-item
                        v-for="menuItem in userMenuItems"
                        :key="menuItem.title"
                        class="user-menu-item"
                        @click="menuItem.action"
                      >
                        <v-list-item-icon>
                          <v-icon :color="menuItem.color">
                            {{ menuItem.icon }}
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>{{
                            menuItem.title
                          }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-menu>
              </template>

              <!-- Login Button for Unauthenticated Users -->
              <template v-else>
                <v-btn
                  rounded
                  color="primary"
                  class="login-btn"
                  elevation="2"
                  @click="loginClick"
                >
                  <v-icon left>mdi-login</v-icon>
                  {{ dialect.Login[$vuetify.lang.current] }}
                </v-btn>
              </template>
            </div>

            <!-- Mobile Menu -->
            <v-btn
              icon
              class="action-btn mobile-menu-btn hidden-lg-and-up ml-2"
              @click="toggleMobileMenu"
            >
              <v-icon>mdi-menu</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-app-bar>

    <!-- Mobile Navigation Drawer -->
    <v-navigation-drawer
      v-model="mobileMenuOpen"
      temporary
      right
      width="300"
      class="mobile-drawer"
    >
      <v-list nav dense>
        <v-list-item
          v-for="navItem in navigationItems"
          :key="navItem.path"
          :to="navItem.path"
          exact
        >
          <v-list-item-icon>
            <v-icon>{{ navItem.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{
              DIALECT[navItem.title][$vuetify.lang.current]
            }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>
  </div>
</template>

<script>
import DIALECT from '@/helpers/dialect.js'
import { StorageService } from '@/helpers/storage'
import { mapGetters } from 'vuex'
import api from '@/api/api'

export default {
  name: 'Header',
  // headerStyle prop removed; layout handled by Vuetify app-bar

  data() {
    return {
      // Menu states
      DIALECT,
      loginMenuOpen: false,
      langMenuOpen: false,
      mobileMenuOpen: false,

      // UI states
      isScrolled: false,
      searchQuery: '',
      auth0CheckInterval: null,
      lastAuthState: false,
      verificationResendLoading: false,
      verificationMessage: '',
      verificationMessageType: 'info',

      // Data
      selectedLocaleIndex: 1,
      defaultLocale: [
        { language: 'English', value: 'en', flag: 'mdi-flag' },
        { language: 'Français', value: 'fr', flag: 'mdi-flag' },
        { language: 'العربية', value: 'ar', flag: 'mdi-flag' }
      ],
      dialect: {
        Login: { en: 'Sign In', fr: 'Connexion', ar: 'تسجيل الدخول' }
      },
      navigationItems: [
        { title: 'Dashboard', path: '/', icon: 'mdi-view-dashboard' },
        { title: 'Inventory', path: '/items', icon: 'mdi-package-variant' },
        { title: 'Reports', path: '/reports', icon: 'mdi-file-chart' }
      ],
      userMenuItems: [
        {
          title: 'Profile Settings',
          icon: 'mdi-account-cog',
          color: 'primary',
          action: () => this.navigateTo('/profile')
        },
        {
          title: 'Preferences',
          icon: 'mdi-cog',
          color: 'primary',
          action: () => this.navigateTo('/settings')
        },
        {
          title: 'Help & Support',
          icon: 'mdi-help-circle',
          color: 'primary',
          action: () => this.navigateTo('/help')
        },
        {
          title: 'Sign Out',
          icon: 'mdi-logout',
          color: 'error',
          action: () => this.logoutClick()
        }
      ]
    }
  },
  computed: {
    ...mapGetters({
      // Removed legacy keycloak getters
      authenticated: 'auth/authenticated',
      fullname: 'auth/fullname',
      email: 'auth/email'
    }),
    // Auth state (Auth0 first)
    isUserAuthenticated() {
      if (localStorage.getItem('user_logged_out') === 'true') return false
      return !!(
        this.$auth0 &&
        !this.$auth0.isLoading &&
        this.$auth0.isAuthenticated
      )
    },
    displayFullname() {
      return this.$auth0?.user?.name || this.fullname || 'User'
    },
    displayEmail() {
      return this.$auth0?.user?.email || this.email || ''
    },
    userAvatar() {
      return this.$auth0?.user?.picture || null
    },
    emailVerificationKnown() {
      return typeof this.$auth0?.user?.email_verified === 'boolean'
    },
    emailVerified() {
      return this.$auth0?.user?.email_verified === true
    },
    emailVerificationLabel() {
      if (!this.emailVerificationKnown) return 'Email status unavailable'
      return this.emailVerified ? 'Email verified' : 'Email not verified'
    },
    emailVerificationIcon() {
      if (!this.emailVerificationKnown) return 'mdi-help-circle-outline'
      return this.emailVerified
        ? 'mdi-check-circle-outline'
        : 'mdi-alert-circle-outline'
    },
    emailVerificationColor() {
      if (!this.emailVerificationKnown) return 'grey'
      return this.emailVerified ? 'success' : 'warning'
    },
    canResendVerificationEmail() {
      return (
        this.emailVerificationKnown &&
        !this.emailVerified &&
        !!this.$auth0?.user?.sub
      )
    },
    userRole() {
      // Prefer roles from localStorage for navigation-only display
      let roles = []
      try {
        const stored = localStorage.getItem('user_roles')
        if (stored) {
          const parsed = JSON.parse(stored)
          if (Array.isArray(parsed)) roles = parsed
        }
      } catch (e) {
        // ignore parse errors
      }

      // Fallback to Auth0 user claims if nothing stored
      if (!roles.length) {
        const user = this.$auth0?.user
        const ns = process.env.VUE_APP_AUTH0_CLAIMS_NAMESPACE
        if (user && ns && user[`${ns}roles`]) {
          roles = user[`${ns}roles`]
        } else if (user) {
          roles = user.roles || user.app_metadata?.roles || []
        }
      }

      if (roles.includes('Owner')) return 'Owner'
      if (roles.includes('Admin')) return 'Admin'
      if (roles.includes('Write') || roles.includes('Manager')) return 'Manager'
      if (roles.includes('Read') || roles.includes('User')) return 'User'
      return 'User'
    },
    truncatedName() {
      const name = this.displayFullname || ''
      return name.length > 15 ? name.substring(0, 15) + '...' : name
    }
  },
  watch: {
    authenticated() {
      this.$nextTick(() => this.$forceUpdate())
    },
    '$auth0.isAuthenticated': {
      handler() {
        this.$nextTick(() => this.$forceUpdate())
      },
      immediate: true
    },
    '$auth0.isLoading': {
      handler() {
        this.$nextTick(() => this.$forceUpdate())
      },
      immediate: true
    },
    '$auth0.user': {
      handler() {
        this.$nextTick(() => this.$forceUpdate())
      },
      immediate: true,
      deep: true
    }
  },
  created() {
    // Set up language
    const savedLang = StorageService.getLanguage()
    const idx = this.defaultLocale.findIndex((l) => l.value === savedLang)
    this.selectedLocaleIndex = idx !== -1 ? idx : 1
    this.$vuetify.lang.current =
      this.defaultLocale[this.selectedLocaleIndex].value

    // Set up scroll listener
    window.addEventListener('scroll', this.handleScroll)

    if (this.$auth0) {
      // Listen for Auth0 state changes if the plugin supports it
      if (this.$auth0.$on) {
        this.$auth0.$on('authStateChanged', () => {
          this.$forceUpdate()
        })
      }

      // Monitor Auth0 loading completion with more frequent checks
      const checkAuth0Ready = () => {
        if (!this.$auth0.isLoading) {
          // Force update after Auth0 is ready
          this.$forceUpdate()
          return
        }
        setTimeout(checkAuth0Ready, 50) // Check more frequently
      }
      checkAuth0Ready()
    }

    // Force reactivity update for user data after auth state is loaded
    this.$nextTick(() => {
      // If user is authenticated on page load, clear the logout flag
      if (this.isUserAuthenticated) {
        localStorage.removeItem('user_logged_out')
      }
    })

    // Restore authentication state on page refresh
    this.restoreAuthState()

    // Set up periodic Auth0 state check (every 1 second) to catch state changes and sync
    this.auth0CheckInterval = setInterval(() => {
      if (this.$auth0 && !this.$auth0.isLoading) {
        // Check if auth state has changed and force update if needed
        const currentAuthState = this.isUserAuthenticated
        if (this.lastAuthState !== currentAuthState) {
          this.lastAuthState = currentAuthState
          this.$forceUpdate()
        }

        // Also run our Auth0 state sync check
        this.checkAuth0State()
      }
    }, 1000) // Check every second for better responsiveness

    // Listen for global auth completion events from CallbackPage
    this.$root.$on('auth0AuthenticationComplete', (authData) => {
      console.log('🎉 Received auth completion event:', authData)
      // Clear logout flag when authentication completes
      localStorage.removeItem('user_logged_out')
      console.log('✅ Auth completion - cleared logout flag')

      this.$forceUpdate()
      this.$nextTick(() => {
        console.log(
          '🔄 Header updated after auth completion, isAuthenticated:',
          this.isUserAuthenticated
        )
      })
    })
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
    if (this.auth0CheckInterval) {
      clearInterval(this.auth0CheckInterval)
    }
    // Clean up global event listeners
    this.$root.$off('auth0AuthenticationComplete')
  },
  methods: {
    async checkAuth0State() {
      /* no-op; removed polling */
    },

    navigateTo(path) {
      this.$router.push(path)
    },

    // Authentication
    async loginClick() {
      if (!this.isUserAuthenticated) {
        // Clear the logout flag when user attempts to login
        localStorage.removeItem('user_logged_out')

        if (this.$auth0) {
          try {
            await this.$auth0.loginWithRedirect({
              appState: { targetPath: '/' }
            })
          } catch (error) {
            console.error('Auth0 login failed:', error)
            // TEMPORARY: Don't redirect to landing during development
            console.log(
              'Would redirect to landing, but skipping for development'
            )
          }
        } else {
          // TEMPORARY: Don't redirect to landing during development
          console.log(
            'Auth0 not available, would redirect to landing but skipping for development'
          )
        }
      }
    },
    async logoutClick() {
      console.log('Logout clicked')

      // Set logout flag to prevent auto-login
      localStorage.setItem('user_logged_out', 'true')

      // Clear Vuex state immediately
      this.$store.commit('auth/SSO_LOADED', undefined)
      StorageService.saveUseSSO(false)

      // Clear all user data from localStorage
      localStorage.removeItem('user_profile')
      localStorage.removeItem('user_display_name')
      localStorage.removeItem('user_org_id')
      localStorage.removeItem('user_roles')
      localStorage.removeItem('user_info')

      // Clear Auth0 session data from localStorage/sessionStorage
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('@@auth0spajs@@') || key.includes('auth0')) {
          localStorage.removeItem(key)
        }
      })
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith('@@auth0spajs@@') || key.includes('auth0')) {
          sessionStorage.removeItem(key)
        }
      })

      // Handle Auth0 logout
      if (this.$auth0 && this.$auth0.auth0Client) {
        try {
          await this.$auth0.auth0Client.logout({ localOnly: true })
          this.$auth0.isAuthenticated = false
          this.$auth0.user = null
          this.$auth0.isLoading = false
          console.log('✅ Auth0 session cleared successfully')
        } catch (error) {
          console.error('Error clearing Auth0 session:', error)
          this.$auth0.isAuthenticated = false
          this.$auth0.user = null
          this.$auth0.isLoading = false
        }
      }

      console.log('🔄 Redirecting to landing page with full reload')
      window.location.href = `${window.location.origin}/landing`
    },

    async resendVerificationEmail() {
      if (!this.$auth0?.user?.sub) {
        this.verificationMessageType = 'error'
        this.verificationMessage = 'Unable to identify the Auth0 user.'
        return
      }

      this.verificationResendLoading = true
      this.verificationMessage = ''

      try {
        await api.post('/auth/resend-verification-email', {})
        this.verificationMessageType = 'success'
        this.verificationMessage = 'Verification email sent.'
      } catch (error) {
        const detail =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          'Could not send verification email.'
        this.verificationMessageType = 'error'
        this.verificationMessage = detail
      } finally {
        this.verificationResendLoading = false
      }
    },

    // Restore authentication state after page refresh (no token from localStorage)
    async restoreAuthState() {
      try {
        await this.$nextTick()
        // If Auth0 indicates an authenticated session but Vuex isn't synced, sync it
        if (this.$auth0 && this.$auth0.isAuthenticated && !this.authenticated) {
          console.log('Syncing Vuex auth state from Auth0...')
          await this.$store.dispatch('auth/authCheck')
        }
      } catch (error) {
        console.warn('Failed to restore auth state:', error)
      }
    },

    // UI interactions

    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen
    },
    handleScroll() {
      this.isScrolled = window.scrollY > 20
    },

    // Language
    changeLocale(locale) {
      this.$vuetify.lang.current = locale.value
      StorageService.saveLanguage(locale.value)
      this.langMenuOpen = false
    }
  }
}
</script>

<style scoped>
/* Header Glass Effect */
.header-glass {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.header-scrolled {
  background: rgba(255, 255, 255, 0.98) !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1) !important;
}

.header-gradient {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
}

.header-container {
  max-width: 100% !important;
}

.header-row {
  width: 100%;
}

/* Brand Section */
.brand-section {
  display: flex;
  align-items: center;
}

.logo-container {
  display: flex;
  align-items: center;
  transition: transform 0.2s ease;
}

.logo-container:hover {
  transform: translateY(-1px);
}

.logo-wrapper {
  position: relative;
  margin-right: 16px;
}

.main-logo {
  transition: filter 0.3s ease;
  z-index: 2;
  position: relative;
}

.logo-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  background: radial-gradient(
    circle,
    rgba(102, 126, 234, 0.2) 0%,
    transparent 70%
  );
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.logo-container:hover .logo-glow {
  opacity: 1;
}

.brand-text {
  display: flex;
  flex-direction: column;
}

.brand-name {
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.brand-tagline {
  font-size: 11px;
  color: #7c8db5;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Navigation */
.main-navigation {
  display: flex;
  gap: 8px;
}

.nav-link {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 12px;
  text-decoration: none;
  color: #64748b;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
}

.nav-link:hover {
  color: #667eea;
  background: rgba(102, 126, 234, 0.08);
  transform: translateY(-1px);
}

.nav-link--active {
  color: #667eea;
  background: rgba(102, 126, 234, 0.12);
  font-weight: 600;
}

.nav-link--active::before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 20px;
  height: 2px;
  background: #667eea;
  border-radius: 2px;
}

.nav-icon {
  margin-right: 6px;
}

/* Action Buttons */
.actions-section {
  display: flex;
  align-items: center;
}

.action-btn {
  width: 44px;
  height: 44px;
  border-radius: 12px !important;
  transition: all 0.2s ease;
  color: #64748b !important;
}

.action-btn:hover {
  background: rgba(102, 126, 234, 0.08) !important;
  color: #667eea !important;
  transform: translateY(-1px);
}

/* Login Button */
.login-btn {
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 12px !important;
  padding: 0 24px !important;
  height: 44px !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  transition: all 0.3s ease !important;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3) !important;
}

.login-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4) !important;
}

/* User Profile */
.user-profile-trigger {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.user-profile-trigger:hover {
  background: rgba(102, 126, 234, 0.08);
}

.user-avatar {
  margin-right: 12px;
  border: 2px solid rgba(102, 126, 234, 0.2);
  transition: border-color 0.2s ease;
}

.user-profile-trigger:hover .user-avatar {
  border-color: #667eea;
}

.user-info {
  display: flex;
  flex-direction: column;
  margin-right: 8px;
}

.user-name {
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.2;
}

.user-role {
  font-size: 12px;
  color: #7c8db5;
  line-height: 1.2;
}

.dropdown-icon {
  color: #7c8db5;
  transition: transform 0.2s ease;
}

.user-profile-trigger:hover .dropdown-icon {
  transform: rotate(180deg);
}

/* Menu Cards */
.language-card,
.user-menu-card {
  border-radius: 16px !important;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1) !important;
}

.user-header {
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 16px 16px 0 0;
}

.user-menu-avatar {
  margin-bottom: 12px;
  border: 3px solid rgba(255, 255, 255, 0.3);
}

.user-details h3 {
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 600;
}

.user-details p {
  margin: 0 0 12px 0;
  opacity: 0.9;
  font-size: 14px;
}

.email-verification-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px 4px;
}

.email-verification-chip {
  flex: 1 1 auto;
  max-width: 100%;
}

.resend-verification-btn {
  flex: 0 0 auto;
  text-transform: none !important;
  font-weight: 600 !important;
}

.verification-message {
  margin: 8px 16px 0 !important;
}

.user-menu-list {
  padding: 8px;
}

.user-menu-item {
  border-radius: 8px;
  margin: 2px 0;
  transition: background 0.2s ease;
}

.user-menu-item:hover {
  background: rgba(102, 126, 234, 0.05);
}

.search-field >>> {
  border-radius: 16px !important;
}

.search-field >>> {
  display: none;
}

/* Mobile Drawer */
.mobile-drawer {
  border-radius: 16px 0 0 16px !important;
}

/* Responsive Design */
@media (max-width: 960px) {
  .brand-text {
    display: none;
  }

  .main-navigation {
    display: none;
  }

  .user-info {
    display: none;
  }
}

@media (max-width: 600px) {
  .header-container {
    padding: 0 16px !important;
  }

  .action-btn {
    width: 40px;
    height: 40px;
  }

  .login-btn {
    height: 40px !important;
    padding: 0 16px !important;
  }
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
